import { create } from 'zustand';
import { supabase } from '@/utils/supabase';

export interface DeliveryConfig {
  freeDeliveryThreshold: number;
  baseDeliveryFee: number;
}

interface DeliverySettingsStore {
  config: DeliveryConfig;
  isLoading: boolean;
  error: string | null;

  loadConfig: () => Promise<void>;
  setConfig: (config: DeliveryConfig) => Promise<void>;
}

const STORAGE_KEY = 'chocolate_vibes_delivery_config';

export const DEFAULT_DELIVERY_CONFIG: DeliveryConfig = {
  freeDeliveryThreshold: 500,
  baseDeliveryFee: 60,
};

function loadFromStorage(): DeliveryConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_DELIVERY_CONFIG, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_DELIVERY_CONFIG };
}

function saveToStorage(config: DeliveryConfig) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch { /* ignore */ }
}

export const useDeliverySettingsStore = create<DeliverySettingsStore>((set) => ({
  config: loadFromStorage(),
  isLoading: false,
  error: null,

  loadConfig: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'delivery_settings')
        .single();

      if (!error && data?.value) {
        const parsed = JSON.parse(data.value);
        const merged = { ...DEFAULT_DELIVERY_CONFIG, ...parsed };
        saveToStorage(merged);
        set({ config: merged, isLoading: false });
        return;
      }
    } catch {
      // Supabase may not be configured or table doesn't exist — fall back to localStorage
    }
    // Fallback: use localStorage only when DB is unavailable
    set({ config: loadFromStorage(), isLoading: false });
  },

  setConfig: async (newConfig) => {
    set({ isLoading: true, error: null });
    try {
      // Save to localStorage first (always works)
      saveToStorage(newConfig);

      // Try to save to Supabase if configured
      await supabase.from('app_settings').upsert({
        key: 'delivery_settings',
        value: JSON.stringify(newConfig),
      });
    } catch {
      // Supabase not available — localStorage is already saved
    }
    set({ config: newConfig, isLoading: false });
  },
}));

// Auto-load on first access
let loaded = false;
export function ensureDeliveryConfigLoaded() {
  if (!loaded) {
    loaded = true;
    useDeliverySettingsStore.getState().loadConfig();
  }
}
